import { useQuery, useQueryClient, useMutation, useInfiniteQuery } from '@tanstack/react-query';
import { signalsApi, billingApi, pricesApi, ApiError } from '~/lib/api';
import { useSignalStream } from '~/hooks/useSignalStream';
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { ErrorBoundary } from '~/components/ErrorBoundary';
import { InlineError } from '~/components/ErrorPage';
import { TradeThreadRow } from '~/components/TradeThreadRow';
import { StatusFilterPill } from '~/components/StatusFilterPill';
import { clsx } from 'clsx';

/** Debounce hook for search input */
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
}

const FILTERS_STORAGE_KEY = 'plays-filters';

/** A trader selected as a filter chip — id drives the query, nick the label. */
interface SelectedTrader {
  trader_id: string;
  current_nick: string;
}

interface PersistedFilters {
  // Two independent toggles. Both off → show every play. Default has
  // followingOn=true so a fresh visitor lands on plays from people they follow.
  followingOn: boolean;
  myPlaysOn: boolean;
  selectedTraders: SelectedTrader[];
  threadStatusFilter: string;
  expiryTypeFilter: string;
}

function loadFilters(): Partial<PersistedFilters> {
  try {
    const raw = localStorage.getItem(FILTERS_STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return {};
}

function saveFilters(filters: PersistedFilters) {
  try {
    localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
  } catch { /* ignore */ }
}

function PlaysPageContent() {
  const queryClient = useQueryClient();

  // SSE for real-time updates - invalidate query when new signal arrives
  const handleSignal = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['signals', 'all'] });
    queryClient.invalidateQueries({ queryKey: ['signals', 'threads'] });
  }, [queryClient]);

  useSignalStream({
    enabled: true,
    onSignal: handleSignal,
  });

  const checkoutMutation = useMutation({
    mutationFn: billingApi.createCheckout,
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });

  const [savedFilters] = useState(loadFilters);
  const [searchInput, setSearchInput] = useState('');
  const [threadStatusFilter, setThreadStatusFilter] = useState<'active' | 'closed' | 'expired' | 'watching' | ''>(
    (savedFilters.threadStatusFilter as 'active' | 'closed' | 'expired' | 'watching' | '') || ''
  );
  const [expiryTypeFilter, setExpiryTypeFilter] = useState<'0dte' | 'swing' | ''>(
    (savedFilters.expiryTypeFilter as '0dte' | 'swing' | '') || ''
  );
  // Only accept persisted entries in the current {trader_id, current_nick}
  // shape. Older builds stored bare nick strings (or a single selectedTrader);
  // those can't be mapped to a trader_id reliably, so drop them.
  const initialTraders: SelectedTrader[] = Array.isArray(savedFilters.selectedTraders)
    ? savedFilters.selectedTraders.filter(
        (t): t is SelectedTrader =>
          !!t && typeof t === 'object' && typeof (t as SelectedTrader).trader_id === 'string',
      )
    : [];
  const [selectedTraders, setSelectedTraders] = useState<SelectedTrader[]>(initialTraders);
  // Default: Following ON, My Plays OFF. Both off = show every play.
  const [followingOn, setFollowingOn] = useState<boolean>(savedFilters.followingOn ?? true);
  const [myPlaysOn, setMyPlaysOn] = useState<boolean>(savedFilters.myPlaysOn ?? false);

  // Persist filters to localStorage
  useEffect(() => {
    saveFilters({ followingOn, myPlaysOn, selectedTraders, threadStatusFilter, expiryTypeFilter });
  }, [followingOn, myPlaysOn, selectedTraders, threadStatusFilter, expiryTypeFilter]);
  const pageSize = 50;

  // Trader autocomplete state
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  const debouncedSearch = useDebounce(searchInput, 300);

  // Fetch trader list for autocomplete
  const { data: tradersData } = useQuery({
    queryKey: ['signals', 'traders'],
    queryFn: signalsApi.traders,
    staleTime: 60000,
  });
  const allTraders = tradersData?.traders || [];

  // Filter suggestions based on input (exclude already-selected traders).
  // allTraders is {trader_id, current_nick}; match on the nick, dedup by id.
  const suggestions = useMemo(() => {
    if (!searchInput.trim()) return [];
    const lower = searchInput.toLowerCase();
    const selectedIds = new Set(selectedTraders.map(t => t.trader_id));
    return allTraders
      .filter(t => !selectedIds.has(t.trader_id) && t.current_nick.toLowerCase().includes(lower))
      .slice(0, 8);
  }, [searchInput, allTraders, selectedTraders]);

  // Close suggestions when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        suggestionsRef.current && !suggestionsRef.current.contains(e.target as Node) &&
        inputRef.current && !inputRef.current.contains(e.target as Node)
      ) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // useInfiniteQuery's queryKey includes all filters, so changing any of
  // them automatically refetches from page 0 — no manual reset needed.
  const handleSearchChange = useCallback((value: string) => {
    setSearchInput(value);
    setHighlightedIndex(-1);
    setShowSuggestions(true);
  }, []);

  const handleSelectTrader = useCallback((trader: { trader_id: string; current_nick: string }) => {
    setSelectedTraders(prev =>
      prev.some(t => t.trader_id === trader.trader_id)
        ? prev
        : [...prev, { trader_id: trader.trader_id, current_nick: trader.current_nick }],
    );
    setSearchInput('');
    setShowSuggestions(false);
    // Don't clear the toggles — explicit trader selection already overrides
    // them on the server, and the UI greys them out via
    // `selectedTraders.length === 0`. Keeping the toggle state means they
    // re-engage automatically when the user removes the trader chip.
  }, []);

  const handleRemoveTrader = useCallback((traderId: string) => {
    setSelectedTraders(prev => prev.filter(t => t.trader_id !== traderId));
  }, []);

  const handleClearTraders = useCallback(() => {
    setSelectedTraders([]);
  }, []);

  const handleThreadStatusChange = useCallback((value: 'active' | 'closed' | 'expired' | 'watching' | '') => {
    setThreadStatusFilter(value);
  }, []);

  const handleExpiryTypeChange = useCallback((value: '0dte' | 'swing' | '') => {
    setExpiryTypeFilter(value);
  }, []);

  // Selecting a trader manually clears the toggles since explicit traders
  // override the toggle-based filter on the backend anyway.
  const toggleFollowing = useCallback(() => {
    setFollowingOn((v) => !v);
    setSelectedTraders([]);
  }, []);
  const toggleMyPlays = useCallback(() => {
    setMyPlaysOn((v) => !v);
    setSelectedTraders([]);
  }, []);

  // Keyboard navigation for suggestions
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(i => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && highlightedIndex >= 0) {
      e.preventDefault();
      handleSelectTrader(suggestions[highlightedIndex]);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  }, [showSuggestions, suggestions, highlightedIndex, handleSelectTrader]);

  // Trader filter resolution. Explicit `selectedTraders` always wins; the
  // backend ignores `following`/`me` when `trader` is sent. Otherwise the
  // toggle state is forwarded to the backend, which builds the candidate
  // list from user_followed_traders + the user's own username.
  const explicitTraderFilter = selectedTraders.length > 0
    ? selectedTraders.map(t => t.trader_id)
    : undefined;
  const sendFollowing = !explicitTraderFilter && followingOn;
  const sendMe = !explicitTraderFilter && myPlaysOn;

  const {
    data: threadsData,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['signals', 'threads', debouncedSearch, threadStatusFilter, expiryTypeFilter, sendFollowing, sendMe, explicitTraderFilter, pageSize],
    queryFn: ({ pageParam }) =>
      signalsApi.threads({
        limit: pageSize,
        offset: pageParam,
        search: debouncedSearch || undefined,
        status: threadStatusFilter || undefined,
        trader: explicitTraderFilter,
        expiry_type: expiryTypeFilter || undefined,
        following: sendFollowing || undefined,
        me: sendMe || undefined,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, p) => sum + p.threads.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
  });

  const threads = threadsData?.pages.flatMap((p) => p.threads) ?? [];
  // Preview rows (the locked teaser shown to non-premium users) are only
  // emitted on the first page.
  const previewThreads = threadsData?.pages[0]?.previewThreads ?? [];

  // Live option premiums for OPEN/PARTIAL positions (drives unrealized %).
  // Keyed by position_id. Refreshed every 5s (Massive last-trade, 5s cache).
  const openContracts = useMemo(
    () => threads
      .filter((t) => t.position_status === 'open' || t.position_status === 'partial')
      .map((t) => ({ key: t.position_id, ticker: t.ticker, strike: t.strike, option_type: t.option_type, expiry: t.expiry })),
    [threads],
  );
  const { data: optionQuotes } = useQuery({
    queryKey: ['optionPrices', openContracts.map((c) => c.key).join(',')],
    queryFn: () => pricesApi.options(openContracts),
    enabled: openContracts.length > 0,
    refetchInterval: 5000,
    staleTime: 5000,
  });

  // Sentinel observer — when the bottom-of-list spacer becomes visible
  // and there are more pages on the server, kick off the next fetch.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasNextPage || isFetchingNextPage) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) fetchNextPage();
      },
      { rootMargin: '200px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-text-primary">Plays</h1>
        <p className="text-sm sm:text-base text-text-secondary">All trading signals from watched channels</p>
      </div>

      {/* Independent toggles. Following ON = traders the user follows;
          My Plays ON = the user's own trades. Both off = every play. */}
      <div className="flex gap-2">
        <button
          onClick={toggleFollowing}
          className={clsx(
            'px-4 py-2 rounded text-sm font-medium font-mono tracking-wide transition-all duration-150',
            followingOn && selectedTraders.length === 0
              ? 'bg-accent text-surface-0'
              : 'bg-surface-3 text-text-secondary hover:text-text-primary'
          )}
        >
          Following
        </button>
        <button
          onClick={toggleMyPlays}
          className={clsx(
            'px-4 py-2 rounded text-sm font-medium font-mono tracking-wide transition-all duration-150',
            myPlaysOn && selectedTraders.length === 0
              ? 'bg-accent text-surface-0'
              : 'bg-surface-3 text-text-secondary hover:text-text-primary'
          )}
        >
          My Plays
        </button>
      </div>

      {/* Filters */}
      <div className="space-y-3 sm:space-y-0 sm:flex sm:gap-4 sm:items-center">
        <div className="relative flex-1 min-w-0">
          {/* Trader pills + search input */}
          <div className="input flex items-center gap-1.5 w-full pr-8 flex-wrap">
            {selectedTraders.map(trader => (
              <span key={trader.trader_id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-mono font-medium uppercase tracking-wider bg-accent/10 text-accent border border-accent/30 whitespace-nowrap shrink-0">
                {trader.current_nick}
                <button
                  onClick={() => handleRemoveTrader(trader.trader_id)}
                  className="hover:text-text-primary transition-colors"
                  aria-label={`Remove ${trader.current_nick} filter`}
                >
                  <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </span>
            ))}
            <input
              ref={inputRef}
              type="text"
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
              onKeyDown={handleKeyDown}
              placeholder={selectedTraders.length > 0 ? 'Search ticker or add trader...' : 'Search ticker or trader...'}
              className="bg-transparent border-none outline-none flex-1 min-w-[100px] text-text-primary placeholder-text-muted/50"
            />
          </div>
          {(searchInput || selectedTraders.length > 0) && (
            <button
              onClick={() => {
                handleSearchChange('');
                handleClearTraders();
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary transition-colors"
              aria-label="Clear search"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}

          {/* Autocomplete suggestions */}
          {showSuggestions && suggestions.length > 0 && (
            <div
              ref={suggestionsRef}
              className="absolute z-50 top-full left-0 right-0 mt-1 rounded-lg border border-border bg-surface-1 overflow-hidden"
            >
              {suggestions.map((trader, i) => (
                <button
                  key={trader.trader_id}
                  onClick={() => handleSelectTrader(trader)}
                  className={clsx(
                    'w-full text-left px-3 py-2 text-sm transition-colors',
                    i === highlightedIndex
                      ? 'bg-accent/20 text-text-primary'
                      : 'text-text-secondary hover:bg-surface-2'
                  )}
                >
                  {trader.current_nick}
                </button>
              ))}
            </div>
          )}
        </div>
        {/* Filter pill row — status pills on the left, expiry pills on the right.
            flex-1 lets the container grow into the remaining width so
            justify-between has room to spread the two groups apart. */}
        <div className="flex flex-wrap gap-3 items-center justify-between flex-1">
          {/* Status filter — same pill component as the trader detail page.
              Default (no pill selected) means "Open" = active + watching. */}
          <div className="flex flex-wrap gap-1">
            <StatusFilterPill
              active={threadStatusFilter === 'active'}
              onClick={() =>
                handleThreadStatusChange(threadStatusFilter === 'active' ? '' : 'active')
              }
              colorClass="text-accent"
              activeBgClass="bg-accent/15"
            >
              Active
            </StatusFilterPill>
            <StatusFilterPill
              active={threadStatusFilter === 'watching'}
              onClick={() =>
                handleThreadStatusChange(threadStatusFilter === 'watching' ? '' : 'watching')
              }
              colorClass="text-blue-400"
              activeBgClass="bg-blue-400/15"
            >
              Watch
            </StatusFilterPill>
            <StatusFilterPill
              active={threadStatusFilter === 'closed'}
              onClick={() =>
                handleThreadStatusChange(threadStatusFilter === 'closed' ? '' : 'closed')
              }
              colorClass="text-loss"
              activeBgClass="bg-loss/15"
            >
              Closed
            </StatusFilterPill>
          </div>
          {/* Expiry filter — same pill component. Default (no pill) = All. */}
          <div className="flex flex-wrap gap-1">
            <StatusFilterPill
              active={expiryTypeFilter === '0dte'}
              onClick={() =>
                handleExpiryTypeChange(expiryTypeFilter === '0dte' ? '' : '0dte')
              }
              colorClass="text-warning"
              activeBgClass="bg-warning/15"
            >
              0DTE
            </StatusFilterPill>
            <StatusFilterPill
              active={expiryTypeFilter === 'swing'}
              onClick={() =>
                handleExpiryTypeChange(expiryTypeFilter === 'swing' ? '' : 'swing')
              }
              colorClass="text-text-secondary"
              activeBgClass="bg-text-secondary/15"
            >
              Swing
            </StatusFilterPill>
          </div>
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
        </div>
      )}

      {error && (
        <InlineError
          error={error instanceof ApiError ? error : (error as Error)}
          onRetry={() => {
            queryClient.invalidateQueries({ queryKey: ['signals', 'threads'] });
          }}
        />
      )}

      {!isLoading && threads.length === 0 && previewThreads.length === 0 && (
        <div className="card p-8 text-center">
          <div className="text-4xl mb-4">📭</div>
          <p className="text-text-secondary">
            {selectedTraders.length > 0
              ? `No trades found for ${selectedTraders.map(t => t.current_nick).join(', ')}.`
              : myPlaysOn && !followingOn
                ? 'No trades found for your username. Your plays will appear here when your Discord username matches the trader.'
                : followingOn
                  ? 'No trades from anyone you follow yet. Visit /traders to follow more.'
                  : debouncedSearch || threadStatusFilter || expiryTypeFilter
                    ? 'No trades match your filters.'
                    : 'No trades yet. Trades will appear here once traders post BTO signals.'}
          </p>
        </div>
      )}

      {/* Trades list */}
      {(threads.length > 0 || previewThreads.length > 0) && (
        <>
          <div className="card overflow-hidden">
            <div className="divide-y divide-border/50">
              {threads.map((thread) => (
                <TradeThreadRow
                  key={thread.position_id}
                  thread={thread}
                  livePremium={optionQuotes?.quotes[thread.position_id]}
                />
              ))}
            </div>

            {/* Faded preview rows inline, same card */}
            {previewThreads.length > 0 && (
              <div className="relative">
                <div className="pointer-events-none select-none">
                  <div className="divide-y divide-border/50 opacity-30 blur-[1px]">
                    {previewThreads.map((thread) => (
                      <TradeThreadRow
                        key={thread.position_id}
                        thread={thread}
                      />
                    ))}
                  </div>
                </div>
                <div className="absolute inset-0 flex items-start justify-center pt-6 bg-gradient-to-b from-transparent via-surface-0/60 to-surface-0/90">
                  <div className="text-center space-y-3">
                    <p className="text-sm text-text-secondary">
                      <span className="text-warning font-semibold">{previewThreads.length}+ older trades</span> are waiting for you
                    </p>
                    <button
                      onClick={() => checkoutMutation.mutate()}
                      disabled={checkoutMutation.isPending}
                      className="px-6 py-3 bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-300 hover:to-yellow-400 text-gray-900 font-bold rounded-lg transition-all shadow-lg shadow-yellow-500/25 hover:shadow-yellow-500/40 hover:scale-105 text-sm sm:text-base pointer-events-auto"
                    >
                      {checkoutMutation.isPending ? 'Loading...' : 'Upgrade to Unlock History'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Infinite-scroll sentinel — triggers fetchNextPage when scrolled
              into view (see the IntersectionObserver effect above). */}
          {isFetchingNextPage && (
            <div className="flex items-center justify-center py-2 text-xs text-text-muted">
              loading…
            </div>
          )}
          <div ref={sentinelRef} className="h-px" aria-hidden="true" />
        </>
      )}
    </div>
  );
}

/** Wrapped with error boundary */
export default function PlaysPage() {
  return (
    <ErrorBoundary>
      <PlaysPageContent />
    </ErrorBoundary>
  );
}
